// Track when a user last visited a given surface, and label items newer
// than that as "new" until they visit again.
//
// Design: one localStorage key per surface (e.g. "timeline"), stores the
// ISO timestamp of the last time we marked the visit "seen". The
// component that renders items compares each item's date against this
// timestamp to decide whether to show the "new" pill.
//
// The visit isn't marked as seen the instant the page mounts — that
// would strip the "new" pill from items you just landed to see. Instead
// we snapshot the previous seen-time on mount and stash it in state.
// When the user navigates away (unmount), we bump the stored value to
// now, so their next visit compares against this one.
import { useEffect, useState } from "react";

const PREFIX = "release_hub_last_visit:";

/** Read the stored last-visit timestamp for a surface. Returns null if
 *  the user has never visited (first-visit → nothing is "new" since
 *  everything would qualify; caller decides how to handle). */
function readLastVisit(surface: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(`${PREFIX}${surface}`);
  } catch {
    return null;
  }
}

function writeLastVisit(surface: string, iso: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${PREFIX}${surface}`, iso);
  } catch {
    /* ignore */
  }
}

/** Returns the timestamp against which items should be compared for
 *  "new since your last visit" — the value that was stored *before*
 *  this mount. Bumps the stored value to now on unmount so the next
 *  visit compares against this one instead. */
export function useLastVisitCutoff(surface: string): string | null {
  const [cutoff] = useState<string | null>(() => readLastVisit(surface));

  useEffect(() => {
    // Update stored value when the component unmounts (user navigated
    // away) OR when the surface key changes. Doing it here means the
    // cutoff read at mount is "the value from the previous visit" —
    // exactly what we want.
    return () => {
      writeLastVisit(surface, new Date().toISOString());
    };
  }, [surface]);

  return cutoff;
}
